import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchLocations } from '../../store/slices/locationsSlice';
import client from '../../api/client';
import FilterBar from '../../components/FilterBar';

const PENALTY_PER_ABSENT = 500;
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_ABBR = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const ATTENDANCE_START = new Date(2026, 5, 1); // June 1 2026

function pad(n) { return String(n).padStart(2, '0'); }

function fmt12(timeStr) {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${pad(m)} ${ampm}`;
}

function minutesDiff(inStr, outStr) {
  if (!inStr || !outStr) return null;
  const [ih, im] = inStr.split(':').map(Number);
  const [oh, om] = outStr.split(':').map(Number);
  return (oh * 60 + om) - (ih * 60 + im);
}

function fmtHours(mins) {
  if (mins == null || mins <= 0) return null;
  return `${Math.floor(mins / 60)}:${pad(mins % 60)}`;
}

// Week: Mon–Sun containing the given date
function getWeekBounds(date) {
  const d = new Date(date);
  const dow = d.getDay(); // 0=Sun
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  const mon = new Date(d); mon.setDate(d.getDate() + mondayOffset);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  return { mon, sun };
}

function isoDate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fmtRange(mon, sun) {
  const fmtD = d => `${pad(d.getDate())}-${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()]}-${d.getFullYear()}`;
  return `${fmtD(mon)}  –  ${fmtD(sun)}`;
}

export default function Attendance() {
  const dispatch = useDispatch();
  const { list: locations } = useSelector(s => s.locations);

  const now = new Date();
  const [weekAnchor, setWeekAnchor] = useState(now); // any date in the displayed week
  const [locationFilter, setLocationFilter] = useState('');
  const [subLocationFilter, setSubLocationFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [allRes, setAllRes] = useState([]);
  const [selectedRE, setSelectedRE] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [loadingAtt, setLoadingAtt] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);

  useEffect(() => { dispatch(fetchLocations()); }, [dispatch]);

  // Load all REs once
  useEffect(() => {
    client.get('/auth/users/', { params: { role: 'RE' } })
      .then(({ data }) => setAllRes(data.results || data))
      .catch(() => setAllRes([]));
  }, []);

  // Sub-location options — compare by location name to handle duplicate location IDs
  const subLocationOptions = locationFilter
    ? [...new Set(allRes.filter(r => r.location_name === locationFilter && r.sub_location).map(r => r.sub_location))].sort()
    : [...new Set(allRes.filter(r => r.sub_location).map(r => r.sub_location))].sort();

  // Filtered RE list
  const res = allRes
    .filter(r => {
      if (locationFilter && r.location_name !== locationFilter) return false;
      if (subLocationFilter && r.sub_location !== subLocationFilter) return false;
      if (agentFilter && String(r.id) !== String(agentFilter)) return false;
      return true;
    })
    .sort((a, b) => (a.employee_id || '').localeCompare(b.employee_id || ''));

  // Keep selectedRE valid
  useEffect(() => {
    if (res.length === 0) { setSelectedRE(null); return; }
    if (!res.find(r => r.id === selectedRE)) setSelectedRE(res[0].id);
  }, [res.map(r => r.id).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const { mon, sun } = getWeekBounds(weekAnchor);

  // Fetch attendance for the displayed week (+ current month for totals)
  const loadAttendance = useCallback(() => {
    if (!selectedRE) return;
    // Fetch from June 1 to end of current month (for totals)
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setLoadingAtt(true);
    client.get('/attendance/', {
      params: {
        user: selectedRE,
        date_from: isoDate(ATTENDANCE_START),
        date_to: isoDate(monthEnd),
      }
    })
      .then(({ data }) => setAttendance(data.results || data))
      .catch(() => setAttendance([]))
      .finally(() => setLoadingAtt(false));
  }, [selectedRE]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadAttendance(); }, [loadAttendance]);

  function getRecord(dateStr) {
    return attendance.find(a => a.date === dateStr) || null;
  }

  // Build week days Mon–Sun
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(mon); d.setDate(mon.getDate() + i);
    return d;
  });

  // Monthly summary (from June 1, unmarked = absent for working days)
  const selectedREObj = allRes.find(r => r.id === selectedRE);
  function getMonthlySummary() {
    let workDays = 0, presentDays = 0, absentDays = 0;
    const d = new Date(ATTENDANCE_START);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    while (d <= today) {
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) {
        workDays++;
        const isToday = isoDate(d) === isoDate(today);
        const rec = getRecord(isoDate(d));
        if (rec && rec.is_present) presentDays++;
        else if (!isToday || rec) absentDays++; // today with no record = pending, not absent
      }
      d.setDate(d.getDate() + 1);
    }
    return { workDays, presentDays, absentDays, penalty: absentDays * PENALTY_PER_ABSENT };
  }

  async function handleSyncNow() {
    setSyncing(true); setSyncMsg(null);
    try {
      const { data } = await client.post('/attendance/sync/');
      setSyncMsg({ ok: true, text: data.message });
      loadAttendance();
    } catch (err) {
      setSyncMsg({ ok: false, text: err.response?.data?.message || 'Sync failed' });
    } finally { setSyncing(false); }
  }

  function prevWeek() { const d = new Date(weekAnchor); d.setDate(d.getDate() - 7); setWeekAnchor(d); }
  function nextWeek() { const d = new Date(weekAnchor); d.setDate(d.getDate() + 7); setWeekAnchor(d); }

  const { workDays, presentDays, absentDays, penalty } = getMonthlySummary();

  const agentDropdownList = allRes
    .filter(r => {
      if (locationFilter && r.location_name !== locationFilter) return false;
      if (subLocationFilter && r.sub_location !== subLocationFilter) return false;
      return true;
    })
    .sort((a, b) => (a.employee_id || '').localeCompare(b.employee_id || ''));

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Attendance</h2>
        <p className="text-gray-500 text-sm">Resident Engineer attendance — ₹{PENALTY_PER_ABSENT.toLocaleString('en-IN')} penalty per absent working day</p>
      </div>

      {/* Filter bar */}
      <FilterBar
        hasActiveFilters={!!(locationFilter || subLocationFilter || agentFilter)}
        onResetAll={() => { setLocationFilter(''); setSubLocationFilter(''); setAgentFilter(''); }}
      >
        <FilterBar.Item value={locationFilter} onClear={() => { setLocationFilter(''); setSubLocationFilter(''); setAgentFilter(''); }}>
          <select value={locationFilter} onChange={e => { setLocationFilter(e.target.value); setSubLocationFilter(''); setAgentFilter(''); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All Locations</option>
            {[...new Set(allRes.filter(r => r.location_name).map(r => r.location_name))]
              .sort((a, b) => a.localeCompare(b))
              .map(name => <option key={name} value={name}>{name}</option>)}
          </select>
        </FilterBar.Item>
        <FilterBar.Item value={subLocationFilter} onClear={() => { setSubLocationFilter(''); setAgentFilter(''); }}>
          <select value={subLocationFilter} onChange={e => { setSubLocationFilter(e.target.value); setAgentFilter(''); }}
            disabled={subLocationOptions.length === 0}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-40">
            <option value="">All Sub-Locations</option>
            {subLocationOptions.map(sl => <option key={sl} value={sl}>{sl}</option>)}
          </select>
        </FilterBar.Item>
        <FilterBar.Item value={agentFilter} onClear={() => setAgentFilter('')}>
          <select value={agentFilter} onChange={e => setAgentFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 min-w-52">
            <option value="">All Agents</option>
            {agentDropdownList.map(r => (
              <option key={r.id} value={r.id}>{r.employee_id ? `${r.employee_id} – ` : ''}{r.full_name}</option>
            ))}
          </select>
        </FilterBar.Item>
        <div className="ml-auto flex items-center gap-3">
          {syncMsg && (
            <span className={`text-sm font-medium ${syncMsg.ok ? 'text-green-600' : 'text-red-500'}`}>
              {syncMsg.ok ? '✓' : '✗'} {syncMsg.text}
            </span>
          )}
          <button onClick={handleSyncNow} disabled={syncing}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {syncing ? 'Syncing…' : 'Sync from Zoho'}
          </button>
        </div>
      </FilterBar>

      {/* Timeline panel */}
      {selectedREObj ? (
        <div className="bg-white rounded-xl shadow overflow-hidden">

          {/* RE selector + week nav + summary */}
          <div className="px-5 py-4 border-b flex items-center gap-4 flex-wrap">
            {/* RE selector */}
            <select value={selectedRE || ''} onChange={e => setSelectedRE(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-semibold outline-none focus:ring-2 focus:ring-blue-500">
              {res.map(r => <option key={r.id} value={r.id}>{r.employee_id ? `${r.employee_id} – ` : ''}{r.full_name}</option>)}
            </select>

            {/* Week nav */}
            <div className="flex items-center gap-2">
              <button onClick={prevWeek} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
              </button>
              <span className="text-sm font-medium text-gray-700 min-w-52 text-center">{fmtRange(mon, sun)}</span>
              <button onClick={nextWeek} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
              </button>
            </div>

            {/* Monthly totals (Jun 1 onwards, unmarked=absent) */}
            <div className="ml-auto flex items-center gap-4 text-sm">
              <span className="text-gray-400 text-xs">Since 1 Jun 2026</span>
              <span className="text-green-600 font-semibold">{presentDays} present</span>
              <span className="text-red-500 font-semibold">{absentDays} absent</span>
              {penalty > 0
                ? <span className="font-bold text-white bg-red-500 rounded-lg px-3 py-1">₹{penalty.toLocaleString('en-IN')} penalty</span>
                : <span className="font-semibold text-green-600 bg-green-50 rounded-lg px-3 py-1">No penalty</span>
              }
            </div>
          </div>

          {/* Week rows */}
          <div className="divide-y divide-gray-50">
            {loadingAtt ? (
              <div className="py-16 text-center text-gray-400">Loading…</div>
            ) : weekDays.map(day => {
              const dateStr = isoDate(day);
              const dow = day.getDay();
              const isWknd = dow === 0 || dow === 6;
              const isToday = dateStr === isoDate(now);
              const isFuture = day > now;
              const beforeStart = day < ATTENDANCE_START;
              const rec = getRecord(dateStr);

              // Determine state
              let state;
              if (isWknd)                        state = 'weekend';
              else if (isFuture || beforeStart)  state = 'future';
              else if (rec?.is_present)          state = 'present';
              else if (isToday && !rec)          state = 'future'; // pending — may update later
              else                               state = 'absent';

              const barColor = {
                present: 'bg-green-400',
                absent:  'bg-red-300',
                weekend: 'bg-amber-200',
                future:  'bg-gray-100',
              }[state];

              const checkIn  = rec?.check_in  ? fmt12(rec.check_in)  : null;
              const checkOut = rec?.check_out ? fmt12(rec.check_out) : null;
              const worked   = fmtHours(minutesDiff(rec?.check_in, rec?.check_out));

              return (
                <div key={dateStr} className={`flex items-center px-5 py-3 gap-4 ${isToday ? 'bg-blue-50/60' : isWknd ? 'bg-amber-50/30' : 'hover:bg-gray-50/60'}`}>
                  {/* Day label */}
                  <div className="w-12 shrink-0 text-right">
                    <div className={`text-xs font-semibold ${isToday ? 'text-blue-500' : 'text-gray-400'}`}>{DAY_ABBR[dow]}</div>
                    <div className={`text-sm font-bold ${isToday ? 'text-blue-700' : 'text-gray-700'}`}>{pad(day.getDate())}</div>
                  </div>

                  {/* Check-in time */}
                  <div className="w-20 shrink-0 text-right">
                    <span className="text-xs text-gray-400">{checkIn || (isWknd ? '' : '—')}</span>
                  </div>

                  {/* Thin bar */}
                  <div className="flex-1 relative flex items-center">
                    <div className={`w-full h-3 rounded-full ${barColor} relative overflow-hidden`}>
                      {isWknd && (
                        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-amber-600">Weekend</span>
                      )}
                    </div>
                  </div>

                  {/* Check-out time */}
                  <div className="w-20 shrink-0 text-left">
                    <span className="text-xs text-gray-400">{checkOut || ''}</span>
                  </div>

                  {/* Hours worked */}
                  <div className="w-24 shrink-0 text-right">
                    {worked ? (
                      <div>
                        <div className="text-sm font-bold text-gray-700">{worked}</div>
                        <div className="text-xs text-gray-400">Hrs worked</div>
                      </div>
                    ) : state === 'present' ? (
                      <div className="text-xs text-green-600 font-medium">Present</div>
                    ) : state === 'absent' ? (
                      <div className="text-xs text-red-400 font-medium">Absent</div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="px-5 py-3 border-t bg-gray-50 flex gap-6 text-xs text-gray-500">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-400 inline-block" /> Present</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-300 inline-block" /> Absent (₹500)</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-200 inline-block" /> Weekend</span>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow p-12 text-center text-gray-400">
          No Resident Engineers found for the selected filters.
        </div>
      )}
    </div>
  );
}
